import { ref, computed, watch, type Ref } from 'vue'

export type ScriptMode = 'roman' | 'native'

const STORAGE_KEY_PREFIX = 'ssi_script_mode_'

/**
 * Toggle between romanized and native script display for target language.
 * Preference is stored per-course in localStorage.
 */
export function useScriptMode(courseCode: Ref<string>) {
  const scriptMode = ref<ScriptMode>('roman')

  // Load saved preference when course changes
  watch(courseCode, (code) => {
    if (!code) return
    let saved: string | null = null
    try {
      saved = localStorage.getItem(`${STORAGE_KEY_PREFIX}${code}`)
    } catch { /* Storage may be denied; use the default for this course. */ }
    scriptMode.value = saved === 'native' ? 'native' : 'roman'
  }, { immediate: true })

  // Persist before a same-tick course change can redirect this choice's write.
  watch(scriptMode, (mode) => {
    if (!courseCode.value) return
    try {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${courseCode.value}`, mode)
    } catch { /* Keep the in-memory choice when persistence is unavailable. */ }
  }, { flush: 'sync' })

  const isNativeScript = computed(() => scriptMode.value === 'native')

  const toggleScriptMode = () => {
    scriptMode.value = scriptMode.value === 'roman' ? 'native' : 'roman'
  }

  return { scriptMode, isNativeScript, toggleScriptMode }
}
